import { type ReactNode, useCallback, useRef } from 'react';
import cn from 'classnames';
import { ClickableIcon, IconClose, VStack } from '@direct-frontend/components';
import { type TestProps } from '@direct-frontend/components/testing';
import { ANIMATION_STATES, type AnimationState, useAnimationState } from '../../lib/animationState';
import { makeModifierClassname } from '../../lib/makeModifierClassname';
import { Overlay } from '../Overlay';
import { ScrollContext } from '../Sticky';
import { DialogProvider } from './DialogContext';
import styles from './Dialog.module.css';

/*
 * При width: auto scale-анимация не до конца отрабатывает (размытый рендеринг).
 * Принудительно перерисовываем модалку после завершения анимации.
 */
const forceRepaint = (element: HTMLElement | null) => {
    if (element) {
        element.style.willChange = 'auto';
    }
};

export interface DialogProps extends TestProps {
    className?: string;
    containerClassName?: string;
    isOpen: boolean;
    isFullscreen?: boolean;
    onClose: VoidFunction;
    children: ReactNode;
    size?: 'xs' | 'auto';
    dismissOnOutsideClick?: boolean;
    closable?: boolean;
    sizes?: {
        width?: number;
        height?: number;
    };
    onAnimationEnd?: (animationState: AnimationState) => void;
}

const dialogModifier = makeModifierClassname(styles, 'dialog');

export const Dialog = (props: DialogProps) => {
    const {
        'data-testid': dataTestId = 'Dialog',
        className,
        containerClassName,
        size,
        isFullscreen,
        isOpen,
        onClose,
        children,
        dismissOnOutsideClick,
        closable = true,
        sizes,
        onAnimationEnd: onAnimationEndProp,
    } = props;
    const contentRef = useRef<HTMLDivElement>(null);

    const handleAnimationEnd = useCallback(
        (state: AnimationState) => {
            if (state === ANIMATION_STATES.OPEN) {
                forceRepaint(contentRef.current);
            }

            onAnimationEndProp?.(state);
        },
        [onAnimationEndProp]
    );

    const { animationState, onAnimationEnd } = useAnimationState({
        target: contentRef,
        isOpen,
        onAnimationEnd: handleAnimationEnd,
    });

    return (
        <DialogProvider isOpen={isOpen} isFullscreen={isFullscreen} onClose={onClose}>
            <Overlay
                data-testid={`${dataTestId}.Overlay`}
                position="center"
                preventScroll={false}
                isOpen={isOpen}
                dismissOnOutsideClick={dismissOnOutsideClick}
                className={styles.overlay}
                onClose={onClose}
            >
                <div
                    ref={contentRef}
                    className={cn(
                        containerClassName,
                        dialogModifier.className,
                        dialogModifier('open', animationState === ANIMATION_STATES.OPEN),
                        dialogModifier('close', animationState === ANIMATION_STATES.CLOSE),
                        dialogModifier('fullscreen', isFullscreen),
                        dialogModifier('xs', size === 'xs'),
                        dialogModifier('auto', size === 'auto')
                    )}
                    style={{
                        width: sizes?.width,
                        height: sizes?.height,
                    }}
                    onAnimationEnd={onAnimationEnd}
                    data-testid={`${dataTestId}.Content`}
                >
                    {closable && (
                        <ClickableIcon
                            role="main"
                            color="white"
                            clickableAreaSize="32"
                            className={styles.closeButton}
                            data-testid={`${dataTestId}.CloseButton`}
                            onClick={onClose}
                        >
                            <IconClose size="28" />
                        </ClickableIcon>
                    )}

                    <ScrollContext.Provider value={{ scrollRef: contentRef, offsetTop: 0 }}>
                        <VStack className={cn(className, styles.inner)}>{children}</VStack>
                    </ScrollContext.Provider>
                </div>
            </Overlay>
        </DialogProvider>
    );
};
